import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Dialog, DialogContent
} from '../components/ui/dialog';
import {
  MagnifyingGlass, ChartBar, ChartLine, Buildings, CreditCard, Receipt,
  Lifebuoy, EnvelopeSimple, FileText, ClockCounterClockwise, FolderOpen,
  Users, User, GearSix, Question, ArrowRight, Plus, UserPlus, SignOut,
  Sun, Moon, Lock
} from '@phosphor-icons/react';

// Static nav items, keyed by role
const STATIC_ROUTES = {
  super_admin: [
    { label: 'Dashboard', path: '/dashboard', icon: ChartBar },
    { label: 'Analytics', path: '/analytics', icon: ChartLine },
    { label: 'Tenants', path: '/tenants', icon: Buildings },
    { label: 'Billing', path: '/billing', icon: CreditCard },
    { label: 'Invoices', path: '/invoices', icon: Receipt },
    { label: 'Support', path: '/admin/support', icon: Lifebuoy },
    { label: 'Inbox · Contact Submissions', path: '/admin/contact-submissions', icon: EnvelopeSimple },
    { label: 'Reports', path: '/billing-reports', icon: FileText },
    { label: 'Activity Log', path: '/audit-logs', icon: ClockCounterClockwise },
    { label: 'My Profile', path: '/profile', icon: User },
    { label: 'Settings', path: '/settings', icon: GearSix },
  ],
  tenant_admin: [
    { label: 'Dashboard', path: '/dashboard', icon: ChartBar },
    { label: 'Analytics', path: '/analytics', icon: ChartLine },
    { label: 'Users', path: '/users', icon: Users },
    { label: 'Projects', path: '/projects', icon: FolderOpen },
    { label: 'Billing', path: '/my-billing', icon: CreditCard },
    { label: 'Invoices', path: '/my-invoices', icon: Receipt },
    { label: 'Support', path: '/support', icon: Lifebuoy },
    { label: 'Activity Log', path: '/audit-logs', icon: ClockCounterClockwise },
    { label: 'My Profile', path: '/profile', icon: User },
    { label: 'Settings', path: '/settings', icon: GearSix },
  ],
  regular_user: [
    { label: 'Dashboard', path: '/dashboard', icon: ChartBar },
    { label: 'Analytics', path: '/analytics', icon: ChartLine },
    { label: 'Projects', path: '/projects', icon: FolderOpen },
    { label: 'My Profile', path: '/profile', icon: User },
    { label: 'Settings', path: '/settings', icon: GearSix },
  ],
};

function score(text, q) {
  if (!q) return 0;
  const t = (text || '').toLowerCase();
  const ql = q.toLowerCase();
  if (t.startsWith(ql)) return 3;
  if (t.includes(ql)) return 2;
  // simple fuzzy: every char of q appears in order in t
  let i = 0;
  for (const ch of t) { if (ch === ql[i]) i++; if (i === ql.length) return 1; }
  return 0;
}

export default function CommandPalette({ open, onClose }) {
  const { user, apiCall, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [dynamic, setDynamic] = useState([]); // dynamic results from API
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const role = user?.role || 'regular_user';
  const isLocked = !!user?.is_locked;

  const baseRoutes = useMemo(() => {
    let items = STATIC_ROUTES[role] || STATIC_ROUTES.regular_user;
    // Filter routes when tenant is locked
    if (isLocked && role !== 'super_admin') {
      const allowed = new Set(['/dashboard', '/my-billing', '/billing', '/audit-logs', '/support', '/profile', '/settings']);
      items = items.filter(i => allowed.has(i.path));
    }
    return items.map(r => ({ ...r, kind: 'nav' }));
  }, [role, isLocked]);

  // Quick actions — role-aware. Navigation+intent actions stash an intent in
  // sessionStorage; destination pages pick it up on mount and open the right dialog.
  const quickActions = useMemo(() => {
    const acts = [];
    const setIntent = (key) => sessionStorage.setItem('roofmate_pending_action', key);
    if (role === 'super_admin') {
      acts.push({ kind: 'action', label: 'Create new tenant', icon: Plus,
        run: () => { setIntent('new-tenant'); navigate('/tenants'); } });
    }
    if (role === 'tenant_admin' && !isLocked) {
      acts.push({ kind: 'action', label: 'Create new project', icon: Plus,
        run: () => { setIntent('new-project'); navigate('/projects'); } });
      acts.push({ kind: 'action', label: 'Invite a user', icon: UserPlus,
        run: () => { setIntent('invite-user'); navigate('/users'); } });
    }
    if (role === 'regular_user' && !isLocked) {
      acts.push({ kind: 'action', label: 'Create new project', icon: Plus,
        run: () => { setIntent('new-project'); navigate('/projects'); } });
    }
    // Universal
    acts.push({
      kind: 'action',
      label: theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode',
      icon: theme === 'light' ? Moon : Sun,
      run: () => toggleTheme(),
    });
    acts.push({
      kind: 'action',
      label: 'Change password',
      icon: Lock,
      run: () => window.dispatchEvent(new CustomEvent('roofmate:open-change-password')),
    });
    acts.push({
      kind: 'action',
      label: 'Sign out',
      icon: SignOut,
      run: () => { logout(); navigate('/login'); },
    });
    return acts;
  }, [role, isLocked, theme, toggleTheme, logout, navigate]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setDynamic([]);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Dynamic search (tenants / projects) — debounced
  useEffect(() => {
    if (!open) return;
    if (!query.trim() || query.trim().length < 2) { setDynamic([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = [];
        if (role === 'super_admin') {
          // Tenants
          try {
            const res = await apiCall('get', '/tenants');
            (res.data || []).forEach(t => {
              if (score(`${t.name} ${t.organization || ''} ${t.email || ''}`, query) > 0) {
                results.push({
                  kind: 'tenant',
                  label: t.name || t.organization || t.email,
                  sublabel: t.email,
                  path: '/tenants',
                  icon: Buildings,
                });
              }
            });
          } catch { /* silent */ }
        } else if (!isLocked) {
          // Projects
          try {
            const res = await apiCall('get', '/projects');
            (res.data || []).forEach(p => {
              if (score(`${p.name} ${p.address || ''}`, query) > 0) {
                results.push({
                  kind: 'project',
                  label: p.name,
                  sublabel: p.address || 'Project',
                  path: `/project/${p.id}`,
                  icon: FolderOpen,
                });
              }
            });
          } catch { /* silent */ }
        }
        setDynamic(results.slice(0, 8));
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open, role, isLocked, apiCall]);

  // Combined + filtered list
  const items = useMemo(() => {
    const q = query.trim();
    const scoredNav = baseRoutes
      .map(r => ({ ...r, _s: q ? score(r.label, q) : 1 }))
      .filter(r => r._s > 0)
      .sort((a, b) => b._s - a._s);
    const scoredActions = quickActions
      .map(a => ({ ...a, _s: q ? score(a.label, q) : 0.5 }))
      .filter(a => a._s > 0)
      .sort((a, b) => b._s - a._s);
    return [...scoredNav, ...scoredActions, ...dynamic];
  }, [baseRoutes, quickActions, dynamic, query]);

  useEffect(() => { setActiveIdx(0); }, [query, dynamic]);

  const choose = useCallback((it) => {
    if (!it) return;
    if (it.kind === 'action' && typeof it.run === 'function') {
      it.run();
    } else if (it.path) {
      navigate(it.path);
    }
    onClose();
  }, [navigate, onClose]);

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(items[activeIdx]); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 overflow-hidden gap-0" data-testid="command-palette">
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 h-14 border-b border-border">
          <MagnifyingGlass size={18} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages, tenants, projects…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/60 border border-border rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-1.5">
          {items.length === 0 && !loading && (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="command-palette-empty">
              <Question size={28} weight="duotone" className="mx-auto mb-2 text-muted-foreground/40" />
              No results for "{query}"
            </div>
          )}

          {items.map((it, i) => {
            const Icon = it.icon || ArrowRight;
            const active = i === activeIdx;
            return (
              <button
                key={`${it.kind}-${it.path || it.label}-${i}`}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => choose(it)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                  active ? 'bg-primary/10 text-foreground' : 'hover:bg-secondary/60 text-foreground/80'
                }`}
                data-testid={`command-palette-item-${i}`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                  active ? 'bg-primary/15 text-primary' : 'bg-muted/60 text-muted-foreground'
                }`}>
                  <Icon size={16} weight={active ? 'fill' : 'regular'} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{it.label}</div>
                  {it.sublabel && <div className="text-[11px] text-muted-foreground truncate">{it.sublabel}</div>}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 shrink-0">{it.kind}</span>
              </button>
            );
          })}

          {loading && (
            <div className="px-4 py-3 text-xs text-muted-foreground">Searching…</div>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-muted/30 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-3">
            <span><kbd className="bg-card border border-border rounded px-1">↑</kbd> <kbd className="bg-card border border-border rounded px-1">↓</kbd> to navigate</span>
            <span><kbd className="bg-card border border-border rounded px-1">↵</kbd> to open</span>
          </span>
          <span>Ctrl + /</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
